import React, { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import ImportExportButtons from '../../components/common/ImportExportButtons';
import { exportToCSV, downloadCSV, parseCSV, readFileAsText, formatDateForCSV } from '../../utils/csvUtils';
import api from '../../utils/apiConfig';
import { useDelivery } from '../../contexts/DeliveryContext';
import { useLocation } from '../../contexts/LocationContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Menu,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  LocalShipping as TrackIcon,
  MoreVert as MoreIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  CheckCircle as DeliveredIcon,
  Pending as PendingIcon,
  DirectionsCar as EnRouteIcon,
  Map as MapIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const Deliveries = () => {
  const { selectedLocation } = useLocation();
  const {
    deliveries,
    stats,
    loading,
    error,
    pagination,
    loadDeliveries,
    getDelivery,
    updateDeliveryStatus,
    deleteDelivery,
    loadStats,
    clearError
  } = useDelivery();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load deliveries when location changes
  useEffect(() => {
    if (selectedLocation?._id) {
      loadDeliveries(selectedLocation._id, {
        search: searchTerm,
        status: statusFilter
      }, page + 1, rowsPerPage);
      loadStats(selectedLocation._id);
    }
  }, [selectedLocation?._id, searchTerm, statusFilter, page, rowsPerPage]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Deliveries',
      value: stats.totalDeliveries || 0,
      icon: <TrackIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      color: '#e3f2fd',
    },
    {
      title: 'Pending',
      value: stats.pendingDeliveries || 0,
      icon: <PendingIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      color: '#fff3e0',
    },
    {
      title: 'In Transit',
      value: stats.inTransitDeliveries || 0,
      icon: <EnRouteIcon sx={{ fontSize: 40, color: '#2196f3' }} />,
      color: '#e3f2fd',
    },
    {
      title: 'Delivered',
      value: stats.deliveredDeliveries || 0,
      icon: <DeliveredIcon sx={{ fontSize: 40, color: '#4caf50' }} />,
      color: '#e8f5e9',
    },
  ];

  const getStatusColor = (status) => {
    const statusColors = {
      'PENDING': 'warning',
      'ASSIGNED': 'info',
      'IN_TRANSIT': 'primary',
      'DELIVERED': 'success',
      'FAILED': 'error',
      'CANCELLED': 'default'
    };
    return statusColors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'PENDING': 'Pending',
      'ASSIGNED': 'Assigned',
      'IN_TRANSIT': 'In Transit',
      'DELIVERED': 'Delivered',
      'FAILED': 'Failed',
      'CANCELLED': 'Cancelled'
    };
    return labels[status] || status;
  };

  const handleMenuOpen = (event, delivery) => {
    setAnchorEl(event.currentTarget);
    setSelectedDelivery(delivery);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = async () => {
    if (selectedDelivery) {
      const fullDelivery = await getDelivery(selectedDelivery.id);
      if (fullDelivery) {
        setSelectedDelivery(fullDelivery);
        setDetailsOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleViewTracking = () => {
    setTrackingOpen(true);
    handleMenuClose();
  };

  const handleStatusChange = async (newStatus) => {
    if (selectedDelivery) {
      await updateDeliveryStatus(selectedDelivery.id, newStatus);
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    if (selectedDelivery && window.confirm('Are you sure you want to delete this delivery?')) {
      await deleteDelivery(selectedDelivery.id);
      handleMenuClose();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // CSV Export Handler
  const handleExport = () => {
    if (!deliveries || deliveries.length === 0) {
      alert('No deliveries to export');
      return;
    }

    const columns = [
      { field: 'id', header: 'Delivery ID' },
      { field: 'order.orderNumber', header: 'Order Number' },
      { field: 'order.customer.name', header: 'Customer Name' },
      { field: 'status', header: 'Status' },
      { field: 'type', header: 'Type' },
      { field: 'priority', header: 'Priority' },
      { field: 'scheduledDate', header: 'Scheduled Date' },
      { field: 'deliveryDate', header: 'Delivery Date' },
      { field: 'deliveryNotes', header: 'Notes' }
    ];

    const exportData = deliveries.map(delivery => ({
      ...delivery,
      scheduledDate: formatDateForCSV(delivery.scheduledDate),
      deliveryDate: formatDateForCSV(delivery.deliveryDate),
      status: getStatusLabel(delivery.status)
    }));

    const csv = exportToCSV(exportData, columns);
    const filename = `deliveries_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
  };

  // CSV Import Handler
  const handleImport = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locationId', selectedLocation._id);

      const response = await api.post('/deliveries/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        alert(response.data.message);
        // Reload deliveries
        loadDeliveries(selectedLocation._id, {}, page + 1, rowsPerPage);
      } else {
        alert(`Import failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error.response?.data?.error || error.message}`);
    }
  };

  if (!selectedLocation) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a location to view deliveries
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        mb: 4 
      }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Delivery Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <ImportExportButtons
            onExport={handleExport}
            onImport={handleImport}
            loading={loading}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            sx={{ fontWeight: 'bold' }}
          >
            Schedule Delivery
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
                backgroundColor: stat.color,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {stat.icon}
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Deliveries Table */}
      <Card>
        <CardContent>
          <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2, 
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <TextField
              placeholder="Search deliveries..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in_transit">In Transit</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer
                sx={{
                  overflowX: 'auto',
                  '& table': {
                    minWidth: { xs: 650, sm: 750 }
                  }
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Delivery ID</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Scheduled Date</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Driver</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No deliveries found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      deliveries.map((delivery) => (
                        <TableRow key={delivery.id} hover>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{delivery.id.substring(0, 8)}...</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {delivery.order?.orderNumber || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}>
                                {delivery.order?.customer?.name?.charAt(0) || 'C'}
                              </Avatar>
                              <Typography variant="body2">
                                {delivery.order?.customer?.name || 'Unknown'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(delivery.status)}
                              color={getStatusColor(delivery.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2">{delivery.type}</Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2">
                              {delivery.scheduledDate
                                ? new Date(delivery.scheduledDate).toLocaleDateString()
                                : 'Not scheduled'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="body2">
                              {delivery.driver
                                ? `${delivery.driver.firstName} ${delivery.driver.lastName}`
                                : 'Unassigned'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, delivery)}
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <MoreIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={pagination.totalItems}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={handleViewTracking}>
          <MapIcon sx={{ mr: 1 }} fontSize="small" />
          Track Delivery
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleStatusChange('in_transit')}>
          <EnRouteIcon sx={{ mr: 1 }} fontSize="small" />
          Mark In Transit
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('delivered')}>
          <DeliveredIcon sx={{ mr: 1 }} fontSize="small" />
          Mark Delivered
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Delivery Details</DialogTitle>
        <DialogContent>
          {selectedDelivery && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Delivery ID
                  </Typography>
                  <Typography variant="body1">{selectedDelivery.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedDelivery.status)}
                    color={getStatusColor(selectedDelivery.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order Number
                  </Typography>
                  <Typography variant="body1">
                    {selectedDelivery.order?.orderNumber || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1">
                    {selectedDelivery.order?.customer?.name || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Delivery Address
                  </Typography>
                  <Typography variant="body1">
                    {selectedDelivery.deliveryAddress?.street || 'N/A'}
                    <br />
                    {selectedDelivery.deliveryAddress?.city}, {selectedDelivery.deliveryAddress?.state}{' '}
                    {selectedDelivery.deliveryAddress?.zipCode}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {selectedDelivery.deliveryNotes || 'No notes'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog
        open={trackingOpen}
        onClose={() => setTrackingOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Track Delivery</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Real-time GPS tracking coming soon...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Deliveries;
